import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Loader, ArrowLeft, ArrowRight, Building } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import Button from "../../components/common/Button";
import professionalProfileApi from "../../api/professionalProfile";
import ProfessionalSearchCard from "../../components/recruiter/ProfessionalSearchCard";
import ProfessionalSideFilters from "../../components/recruiter/ProfessionalSideFilters";
import * as Sentry from "@sentry/react";
import useFieldChoices from "../../hooks/useFieldChoices";

const ProfessionalSearchPage = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        pilot_type: "",
        aircraft_type: "",
        min_hours: "",
        max_hours: "",
        total_hours: "",
        aviation_category: "",
        page: page,
    });

    // Fetch field choices for pilot types
    const { fieldChoices } = useFieldChoices()

    const {
        data: searchResponse,
        isLoading,
        isError,
        error,
    } = useQuery({
        queryKey: ["professional-search", filters],
        queryFn: async () => {
            const res = await professionalProfileApi.searchProfessionals(filters);
            return res.data;
        },
        keepPreviousData: true,
        onError: (err) => {
            Sentry.captureException(err, "Professional search error");
        }
    });

    const professionals = searchResponse?.results || [];
    const pagination = {
        count: searchResponse?.count || 0,
        next: searchResponse?.next || null,
        previous: searchResponse?.previous || null,
        currentPage: page,
    };

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value, page: 1 }));
        setPage(1);
    };

    const resetFilters = () => {
        setFilters({
            pilot_type: "",
            aircraft_type: "",
            min_hours: "",
            max_hours: "",
            total_hours: "",
            aviation_category: "",
            page: 1,
        });
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setFilters((prev) => ({ ...prev, page: newPage }));
        setPage(newPage);
    };

    return (
        <div className="bg-secondary-50 min-h-screen">
            <div className="max-w-full mx-auto px-6 lg:px-8 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-secondary-900">Find Aviation Professionals</h1>
                    <p className="text-secondary-600 mt-2">Search through our global network of verified aviation experts.</p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8">
                    <ProfessionalSideFilters
                        filters={filters}
                        handleFilterChange={handleFilterChange}
                        resetFilters={resetFilters}
                        pilotTypes={fieldChoices?.pilot_type_choices}
                        aviationCategoryChoices={fieldChoices?.aviation_category_choices}
                    />

                    <main className="lg:col-span-8 xl:col-span-9">
                        <div className="mb-6 flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-secondary-800">
                                {pagination.count} Professionals Found
                            </h2>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-20 flex flex-col items-center">
                                <Loader className="w-12 h-12 text-primary-500 animate-spin" />
                                <p className="mt-4 text-secondary-600">Searching profiles...</p>
                            </div>
                        ) : isError ? (
                            <div className="text-center py-10 bg-white rounded-2xl border border-red-100 shadow-sm">
                                {error?.response?.data?.verification_required ? (
                                    <>
                                        <h3 className="text-lg font-bold text-red-600 mb-2">Verification Required</h3>
                                        <p className="text-secondary-600 max-w-md mx-auto">
                                            {error.response.data.message}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-red-600 font-semibold px-4">
                                            {error?.response?.data?.message || error?.message || "Failed to load professionals. You might need an active subscription."}
                                        </p>
                                        {error?.response?.status === 403 && (
                                            <Button
                                                onClick={() => navigate('/pricing')}
                                                className="mt-4"
                                                variant="primary"
                                            >
                                                Upgrade Plan
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {professionals.length > 0 ? (
                                    <div className="grid md:grid-cols-1 xl:grid-cols-2 gap-6">
                                        {professionals.map((prof) => (
                                            <ProfessionalSearchCard
                                                key={prof.id}
                                                professional={prof}
                                                onClick={() => navigate(`/candidate-profile/${prof.id}?type=profile`)}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-20 bg-white rounded-2xl shadow-lg border border-gray-100">
                                        <div className="w-20 h-20 bg-secondary-50 rounded-full flex items-center justify-center mx-auto mb-6">
                                            <User className="w-10 h-10 text-secondary-300" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-secondary-900">No Professionals Found</h3>
                                        <p className="mt-2 text-secondary-600 max-w-md mx-auto">
                                            Try broadening your search criteria or checking back later for new talent.
                                        </p>
                                        <Button onClick={resetFilters} variant="outline" className="mt-8">
                                            Reset All Filters
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {pagination.count > 0 && professionals.length > 0 && (
                            <div className="mt-12 py-6 border-t border-gray-100 flex justify-between items-center">
                                <Button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={!pagination.previous}
                                    variant="secondary"
                                    size="sm"
                                    icon={<ArrowLeft size={16} />}
                                >
                                    Previous
                                </Button>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-secondary-900">
                                        Page {pagination.currentPage}
                                    </span>
                                    <span className="text-sm text-secondary-500">
                                        of {Math.ceil(pagination.count / 10)}
                                    </span>
                                </div>
                                <Button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={!pagination.next}
                                    variant="secondary"
                                    size="sm"
                                    icon={<ArrowRight size={16} />}
                                    iconPosition="right"
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default ProfessionalSearchPage;
